import React, { Component, ReactNode } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { plural } from "@formant/core/common/plural";
import { parseIsoDate } from "@formant/date-fns/parseIsoDate";
import { duration } from "@formant/core/model/duration";
import { ICommand } from "@formant/core/model/ICommand";
import { ICommandTemplate } from "@formant/core/model/ICommandTemplate";
import { Uuid } from "@formant/core/model/Uuid";
import { Icon } from "@formant/ui/component/common/Icon";
import { LoadingIndicator } from "@formant/ui/component/common/LoadingIndicator";
import { animate } from "@formant/ui/utils/animate";

import { track } from "../../analytics";
import {
    appStore,
    commandStore,
    commandTemplateStore,
    deviceDetailsStore,
    deviceStore,
    routerStore,
} from "../../stores";
import { formatDateTime } from "../../utils/formatDateTime";
import { Button } from "../common/Button";
import { Heading2 } from "../common/Heading2";
import { Popup } from "../common/Popup";
import { TextButton } from "../common/TextButton";
import { Timer } from "../common/Timer";
import * as styles from "./IssueCommandPopup.scss";

export interface IIssueCommandPopupProps {
    deviceId: Uuid;
}

@observer
export class IssueCommandPopup extends Component<IIssueCommandPopupProps> {
    @observable
    private visible: boolean = true;

    public render() {
        const { deviceId } = this.props;
        const device = deviceDetailsStore.get(deviceId);
        const { routeTo } = routerStore;
        const commandTemplates = device
            ? commandTemplateStore.listMatching(device.tags)
            : undefined;
        const loading = commandTemplates === undefined;

        const commands = commandStore.list(deviceId) || [];
        const pending = commands.length;

        return (
            <Popup
                visible={this.visible}
                onClose={this.onClose}
                className={classnames(
                    styles["issue-command-popup"],
                    loading && styles.loading,
                    pending && styles.pending,
                )}
            >
                <Timer interval={3 * duration.second} onTick={this.onTick} />
                <LoadingIndicator visible={loading} />
                <Heading2>Issue a Command</Heading2>
                <p className={styles.undelivered}>
                    {pending} undelivered {plural("command", pending)} pending.{" "}
                    <TextButton
                        onClicked={this.onCancelCommands}
                        context={commands}
                    >
                        Cancel pending commands
                    </TextButton>
                </p>
                <p>Choose a command</p>
                <ul>
                    {(commandTemplates || []).map((_, i) => (
                        <li key={i}>
                            <Button
                                className={styles.button}
                                context={_}
                                onClicked={this.onNewCommand}
                                data-testid={`commands-option-${_.name}`}
                            >
                                <Icon name="command" />
                                {_.name}
                            </Button>
                        </li>
                    ))}
                    <li>
                        <Button
                            to={routeTo.createCommandTemplate()}
                            className={styles.button}
                        >
                            <Icon name="plus" />
                            Add new command
                        </Button>
                    </li>
                </ul>
            </Popup>
        );
    }

    private onTick = async () => {
        await commandStore.fetchList(this.props.deviceId);
    };

    private onCancelCommands = (commands: ICommand[]) => {
        const quantity = commands.length;
        const confirmMessage = (
            <>
                <p>
                    This will attempt to cancel {quantity}{" "}
                    {plural("command", quantity)}:
                </p>
                <ul>
                    {commands.map(
                        ({ command, createdAt, commandTemplateId }, i) => {
                            const commandTemplate =
                                !!commandTemplateId &&
                                commandTemplateStore.get(commandTemplateId);
                            const commandTemplateName =
                                (commandTemplate && commandTemplate.name) ||
                                command;
                            return (
                                <li key={i}>
                                    {commandTemplateName} at{" "}
                                    {formatDateTime(
                                        parseIsoDate(defined(createdAt)),
                                    )}
                                </li>
                            );
                        },
                    )}
                </ul>
                <p>Commands delivered to the device cannot be canceled.</p>
                <p>Continue?</p>
            </>
        );
        appStore.setConfirm({
            title: `Cancel ${plural("Command", quantity)}`,
            message: confirmMessage,
            onOk: async () => {
                const cancelResult = (await Promise.all(
                    commands.map(command =>
                        commandStore.cancel(defined(command.id)),
                    ),
                )) as ICommand[];
                // Commands with delivery timestamp have been delivered and therefore were not canceled
                const delivered = cancelResult.filter(
                    _ => _.deliveredAt,
                ).length;
                const canceled = quantity - delivered;
                let snackbarMessage: ReactNode = `Canceled ${canceled} ${plural(
                    "command",
                    canceled,
                )}`;
                if (delivered) {
                    snackbarMessage = (
                        <>
                            <p>{snackbarMessage}</p>
                            <p>{delivered} already delivered</p>
                        </>
                    );
                }
                appStore.setSnackbar(snackbarMessage);
                track("commands canceled", cancelResult);
            },
            okCopy: `Cancel ${quantity} ${plural("Command", quantity)}`,
            cancelCopy: "Do not cancel",
        });
    };

    private onNewCommand = async (command: ICommandTemplate) => {
        await this.onClose();
        deviceStore.issuingCommand = true;
        appStore.commandTemplate = command;
    };

    private onClose = async () => {
        this.visible = false;
        await animate();
        deviceStore.issuingCommand = false;
    };
}
